import React from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { useDispatch } from 'react-redux';
import CardFace from '../Card/CardFace';
import { viewCard } from '../Card/actions';
import { PlainButton } from '../Controls';
import { CardLayout, CardRow, ConditionRule, Details, Stat } from './styled';
import { conditionDisplay } from '../Card/conditionDisplay';

/**
 * The card a menu is about, big enough to read, with its remaining HP, what's attached to it and the cards it evolved
 * from (as cards), and any protection from attacks when it is in play.
 * Tapping the card shows it full screen.
 */
/** Small cards in a row, each tappable to see full size. */
const CardsInRow = ({ cards }) => {
  const dispatch = useDispatch();

  return (
    <CardRow>
      {cards.map((shown, i) => (
        <PlainButton key={i} onClick={() => dispatch(viewCard(shown))} aria-label={`Show ${shown.get('name')}`}>
          <CardFace card={shown} full />
        </PlainButton>
      ))}
    </CardRow>
  );
};

CardsInRow.propTypes = {
  cards: PropTypes.instanceOf(List).isRequired,
};

const CardDetail = ({
  card, damage, damageSeed, energyAttachments, evolvedFrom, protection, hindrance, hinderedBy, attachedTrainers,
  conversions, conditions, conditionRules, children,
}) => {
  const dispatch = useDispatch();
  const hp = card && card.get('hp');
  // What's attached, as the cards themselves: energy, then Trainers like PlusPower. The board marks them with
  // symbols and badges; here they're the cards.
  const attached = (energyAttachments || List()).map(attachment => attachment.get('card')).filter(shown => !!shown)
    .concat(attachedTrainers || List());
  const inPlay = !!energyAttachments;
  // Its markers (poison) show on the card; turning it sideways for Asleep or Paralyzed is for the board, so here the
  // card stays upright and every condition is listed in words.
  const named = conditions ? conditions.toArray() : [];
  const markersOnly = List(named.filter(condition => conditionDisplay([condition]).markers.length > 0));

  return (
    <CardLayout>
      <PlainButton onClick={() => card && dispatch(viewCard(card))} aria-label="Show card full screen">
        <CardFace
          card={card}
          damage={damage}
          damageSeed={damageSeed}
          protection={protection}
          hindrance={hindrance}
          hinderedBy={hinderedBy}
          conversions={conversions}
          conditions={markersOnly}
        />
      </PlainButton>
      <Details>
        {hp > 0 && damage !== undefined && <Stat>HP <b>{Math.max(hp - (damage || 0), 0)}</b> of {hp}</Stat>}
        {named.map((condition) => {
          const rule = conditionRules ? conditionRules.get(condition) : null;
          return rule
            ? <ConditionRule key={condition}><summary title={rule}>{condition}</summary><p>{rule}</p></ConditionRule>
            : <Stat key={condition}><b>{condition}</b></Stat>;
        })}
        {inPlay && !attached.size && <Stat>Nothing attached</Stat>}
        {attached.size > 0 && <Stat>Attached<CardsInRow cards={attached} /></Stat>}
        {evolvedFrom && evolvedFrom.size > 0 && <Stat>Evolved from<CardsInRow cards={evolvedFrom} /></Stat>}
        {protection && <Stat>{protection} this turn</Stat>}
        {hinderedBy && <Stat>{hinderedBy}: {hindrance} this turn</Stat>}
        {conversions && conversions.map(text => <Stat key={text}>{text} (Conversion)</Stat>)}
        {children}
      </Details>
    </CardLayout>
  );
};

CardDetail.propTypes = {
  card: PropTypes.instanceOf(Map),
  /** Trainers attached to a Pokémon in play, like PlusPower, as catalog cards (useCards). */
  attachedTrainers: PropTypes.instanceOf(List),
  conversions: PropTypes.instanceOf(List),
  /** Its Special Conditions by name ("Poisoned"), and what each does (by name, from the server). */
  conditions: PropTypes.instanceOf(List),
  conditionRules: PropTypes.instanceOf(Map),
  damage: PropTypes.number,
  /** Which Pokémon in play this is (Card/gemLayout's gemSeed), so its damage counters sit as they do on the board. */
  damageSeed: PropTypes.number,
  energyAttachments: PropTypes.instanceOf(List),
  /** The cards a Pokémon in play evolved from, oldest first (its devolve attachments' cards). */
  evolvedFrom: PropTypes.instanceOf(List),
  /** Like "Protected from damage", from the server. */
  protection: PropTypes.string,
  /** From an opponent's attack, like Amnesia: what it stops ("Can't use Ember") and the attack's name. */
  hindrance: PropTypes.string,
  hinderedBy: PropTypes.string,
  children: PropTypes.node,
};

export default CardDetail;
